<template>
  <section>
    <div
      class="px-4 mx-auto max-w-3xl lg:px-0"
      :class="[
        paddingTop === 'reduced' ? 'pt-6 lg:pt-6' : 'pt-3 lg:pt-6',
        paddingBottom === 'reduced' ? 'pb-6 lg:pb-6' : 'pb-6 lg:pb-12'
      ]"
    >
      <div class="flex flex-col gap-4 w-full">
        <div class="flex gap-3 items-center md:gap-5">
          <div class="relative shrink-0 size-9">
            <span
              class="absolute inset-0 rounded-full red-flag-badge-pulse bg-lifeboost-red"
              aria-hidden="true"
            />
            <div class="flex relative z-10 justify-center items-center text-base leading-7 text-white rounded-full size-9 bg-lifeboost-red font-ww-regular">
              {{ badgeNumber }}
            </div>
          </div>
          <span
            class="text-xs tracking-wider leading-5 uppercase text-[#F1574B] font-ww-regular"
            v-html="badgeText"
          />
        </div>

        <div class="pt-1.5">
          <h2
            class="text-2xl font-bold leading-tight sm:text-3xl lg:text-34 lg:leading-42 font-ww-bold text-[#3B2314]"
            v-html="title"
          />
        </div>

        <div
          class="pb-4 pt-2 text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5"
          v-html="introContent"
        />

        <div
          v-if="calloutTitle || calloutContent"
          class="relative overflow-hidden rounded-b-lg border border-lifeboost-red border-t-4 bg-[#F0E8D8] px-7 pt-8 pb-6"
        >
          <div class="flex relative z-10 flex-col gap-2.5">
            <h3
              v-if="calloutTitle"
              class="text-xl font-bold leading-8 text-[#3B2314] font-ww-bold"
              v-html="calloutTitle"
            />

            <div
              v-if="calloutContent"
              class="text-15 leading-6 text-[#4A4A4A] font-ww-regular [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-0 [&_strong]:font-ww-bold [&_p+p]:mt-4 [&_sup]:text-xs"
              v-html="calloutContent"
            />
          </div>
        </div>

        <div
          v-if="outroContent"
          class="pt-3 text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5 [&_sup]:text-xs [&_em]:italic [&_strong]:font-ww-bold [&_strong]:text-xl [&_strong]:text-[#3B2314]"
          v-html="outroContent"
        />

        <div
          v-if="showDivider"
          class="mt-10 h-px w-full bg-[#E0D5C5]"
          aria-hidden="true"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  badgeNumber: {
    type: String,
    default: '2'
  },
  badgeText: {
    type: String,
    default: 'Red Flag No. 2'
  },
  title: {
    type: String,
    default: 'The Male Brain Crisis No One Talks About (But Every Man Feels)'
  },
  introContent: {
    type: String,
    default: `<p>Men experience faster age-related cognitive decline than women.</p>
<p>Memory. Processing speed. Spatial skills.</p>
<p>All decline earlier and more sharply.</p>
<p>But studies show:</p>`
  },
  calloutTitle: {
    type: String,
    default: 'Studies have shown regular coffee drinkers have:'
  },
  calloutContent: {
    type: String,
    default: `<ul>
  <li>29% lower risk of Parkinson's</li>
  <li>28% lower risk of dementia</li>
  <li>Better reaction time, attention, and learning</li>
  <li>Stronger short- and long-term memory <sup>15</sup></li>
</ul>`
  },
  outroContent: {
    type: String,
    default: `<p>A 2020 study in Nutrients found that caffeine increases brain connectivity and improves executive function.</p>
<p>Another study in <em>The Journal of Alzheimer's Disease</em> found that coffee's polyphenols help reduce amyloid-beta accumulation — a key factor in Alzheimer's.</p>
<p>And when you combine clean coffee with functional ingredients like lion's mane, chaga, coffee-fruit extract, and Alpha-GPC?</p>
<p>You get a daily ritual that is shown to actively support BDNF (brain-derived neurotrophic factor), neural resilience, and cognitive performance.<sup>16</sup></p>
<p><strong>Mindflow, Cognition Coffee, Regular Coffee</strong></p>
<p>Call it the Lifeboost brain health trifecta, call it delicious, call it a part of your daily routine…I call it a delicious brain biohack!</p>
<p>Each of these products are truly powerful ways to support the health of your brain.</p>`
  },
  showDivider: {
    type: Boolean,
    default: false
  },
  paddingBottom: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'reduced'].includes(value)
  },
  paddingTop: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Red Flag Brain Health Section',
  inputs: [
    {
      name: 'badgeNumber',
      type: 'text',
      defaultValue: '2'
    },
    {
      name: 'badgeText',
      type: 'text',
      defaultValue: 'Red Flag No. 2'
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: 'The Male Brain Crisis No One Talks About (But Every Man Feels)'
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p>Men experience faster age-related cognitive decline than women.</p>
<p>Memory. Processing speed. Spatial skills.</p>
<p>All decline earlier and more sharply.</p>
<p>But studies show:</p>`
    },
    {
      name: 'calloutTitle',
      type: 'text',
      defaultValue: 'Studies have shown regular coffee drinkers have:'
    },
    {
      name: 'calloutContent',
      type: 'richText',
      defaultValue: `<ul>
  <li>29% lower risk of Parkinson's</li>
  <li>28% lower risk of dementia</li>
  <li>Better reaction time, attention, and learning</li>
  <li>Stronger short- and long-term memory <sup>15</sup></li>
</ul>`
    },
    {
      name: 'outroContent',
      type: 'richText',
      defaultValue: `<p>A 2020 study in Nutrients found that caffeine increases brain connectivity and improves executive function.</p>
<p>Another study in <em>The Journal of Alzheimer's Disease</em> found that coffee's polyphenols help reduce amyloid-beta accumulation — a key factor in Alzheimer's.</p>
<p>And when you combine clean coffee with functional ingredients like lion's mane, chaga, coffee-fruit extract, and Alpha-GPC?</p>
<p>You get a daily ritual that is shown to actively support BDNF (brain-derived neurotrophic factor), neural resilience, and cognitive performance.<sup>16</sup></p>
<p><strong>Mindflow, Cognition Coffee, Regular Coffee</strong></p>
<p>Call it the Lifeboost brain health trifecta, call it delicious, call it a part of your daily routine…I call it a delicious brain biohack!</p>
<p>Each of these products are truly powerful ways to support the health of your brain.</p>`
    },
    {
      name: 'showDivider',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'default'
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'default'
    }
  ]
})
</script>
